import { getEnv } from "@/config/env";
import { Template } from "@/types/models";

const PROMPT_LIBRARY_API_URL = getEnv("VITE_PROMPT_LIBRARY_API_URL") || "http://localhost:8005";

let getTokenFn: (() => Promise<string | null>) | null = null;

export function setPromptLibraryTokenGetter(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

async function promptLibraryApiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getTokenFn ? await getTokenFn() : null;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(`${PROMPT_LIBRARY_API_URL}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `API error: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

const promptLibraryApi = {
  get: <T>(path: string) => promptLibraryApiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    promptLibraryApiFetch<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    promptLibraryApiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => promptLibraryApiFetch<T>(path, { method: "DELETE" }),
};

export const templatesApi = {
  // Read operations
  getTemplates: (): Promise<Template[]> =>
    promptLibraryApi.get("/api/prompts/templates"),

  getTemplate: (id: string): Promise<Template> =>
    promptLibraryApi.get(`/api/prompts/templates/${id}`),

  // Admin CRUD operations
  createTemplate: (data: { id: string; name: string; description?: string; is_active?: boolean; template_type_id?: number | null; template_formatetype_ids?: number[] | null }): Promise<Template> =>
    promptLibraryApi.post("/api/prompts/templates", data),

  updateTemplate: (id: string, data: Partial<Template>): Promise<Template> =>
    promptLibraryApi.put(`/api/prompts/templates/${id}`, data),

  deleteTemplate: (id: string): Promise<void> =>
    promptLibraryApi.delete(`/api/prompts/templates/${id}`),
};
